import * as THREE from 'three';

export interface LabelPlacement {
  /** CSS appended after the label is moved onto the point, e.g. to centre or lift it. */
  anchor?: string;
  /** Keeps the whole label this many px inside the viewport instead of hiding it at the edge. */
  clampPx?: number;
}

/** An HTML element pinned over a point of the scene, moved by writing its transform. */
export class ScreenLabel {
  private element: HTMLElement | null = null;
  private transform: string | null = null;
  private readonly projected = new THREE.Vector3();

  constructor(private readonly placement: LabelPlacement = {}) {}

  attach(element: HTMLElement | null): void {
    this.element = element;
    this.transform = null;
  }

  place(point: THREE.Vector3 | null, camera: THREE.Camera, width: number, height: number): void {
    const element = this.element;
    if (!element) return;
    const transform = point ? this.transformFor(point, camera, width, height, element) : '';
    if (transform === this.transform) return;
    this.transform = transform;
    element.style.visibility = transform ? 'visible' : 'hidden';
    element.style.transform = transform;
  }

  private transformFor(
    point: THREE.Vector3,
    camera: THREE.Camera,
    width: number,
    height: number,
    element: HTMLElement,
  ): string {
    this.projected.copy(point).project(camera);
    if (Math.abs(this.projected.z) > 1) return '';
    let x = (this.projected.x * 0.5 + 0.5) * width;
    let y = (-this.projected.y * 0.5 + 0.5) * height;
    const { anchor = 'translate(-50%, -50%)', clampPx } = this.placement;
    if (clampPx !== undefined) {
      const halfWidth = element.offsetWidth / 2;
      const halfHeight = element.offsetHeight / 2;
      x = THREE.MathUtils.clamp(x, clampPx + halfWidth, Math.max(clampPx + halfWidth, width - clampPx - halfWidth));
      y = THREE.MathUtils.clamp(y, clampPx + halfHeight, Math.max(clampPx + halfHeight, height - clampPx - halfHeight));
    }
    return `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) ${anchor}`;
  }
}
